// Line Entry — step 1 of "From another PO…" (canvas board 11): pick the
// source document(s). The list is the source's `documents.choose` lookup +
// filter, answered by the engine (`list_documents`), so it is scoped to this
// record's header (another PO of the SAME supplier). A document already on
// this record shows ticked and fixed. The user then adds every line of the
// chosen documents at once, or goes on to choose lines.

import { useCallback, useEffect, useState } from 'react'
import { Loader2, Search, X } from 'lucide-react'
import { Z_RECORD_DIALOG } from '../../layers'
import type { LineEntryApi, ListDocumentsResponse } from './contract'
import type { LineCmd } from './useLineEntry'
import { T, btn, btnPri, chip, dialog, dialogFoot, overlay, INFO_BAND } from './styles'

interface Props {
  api: LineEntryApi
  cmd: LineCmd
  /** What a document is called ("PO lines" → shown in the copy). */
  noun: string
  onCancel: () => void
  /** `pick`: open the picker over these documents; `all`: add every line. */
  onChoose: (documents: unknown[], mode: 'pick' | 'all') => void
  onError: (what: string, err: unknown) => void
}

export default function DocumentChooser({ api, cmd, noun, onCancel, onChoose, onError }: Props) {
  const [res, setRes] = useState<ListDocumentsResponse | null>(null)
  const [search, setSearch] = useState('')
  const [loading, setLoading] = useState(true)
  const [picked, setPicked] = useState<unknown[]>([])

  const load = useCallback(async (q: string) => {
    setLoading(true)
    try {
      setRes(await api.documents(cmd.name, q))
    } catch (err) {
      onError('Loading documents failed', err)
    } finally {
      setLoading(false)
    }
  }, [api, cmd.name, onError])

  useEffect(() => {
    const h = setTimeout(() => { void load(search) }, search ? 300 : 0)
    return () => clearTimeout(h)
  }, [search, load])

  const multi = res?.multi !== false
  const toggle = (id: unknown) => setPicked(prev => {
    const has = prev.some(p => String(p) === String(id))
    if (!multi) return has ? [] : [id]
    return has ? prev.filter(p => String(p) !== String(id)) : [...prev, id]
  })
  const pickLines = cmd.picker !== 'none'
  const none = picked.length === 0

  return (
    <div role="presentation" onClick={onCancel} style={{ ...overlay, zIndex: Z_RECORD_DIALOG, paddingTop: 60 }}>
      <div role="dialog" aria-modal="true" aria-labelledby="le-doc-title" onClick={e => e.stopPropagation()}
        style={{ ...dialog, maxWidth: 960 }}>
        <div style={{ padding: '18px 22px 14px', display: 'flex', flexDirection: 'column', gap: 8,
          borderBottom: `1px solid ${T.border}` }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            {pickLines && <span style={{ fontSize: 13, color: T.muted, fontWeight: 600 }}>Step 1 of 2</span>}
            <span style={{ flex: 1 }} />
            <button type="button" onClick={onCancel} aria-label="Close" style={{ ...btn, padding: '0 10px' }}>
              <X size={16} />
            </button>
          </div>
          <h2 id="le-doc-title" style={{ margin: 0, fontSize: 19, fontWeight: 600 }}>{cmd.label.replace(/…$/, '')}</h2>
          <p style={{ margin: 0, fontSize: 14, color: T.muted }}>
            Tick {multi ? 'one or more' : 'one'}{pickLines ? '; you choose the lines next.' : '; every line is added.'}
          </p>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, height: 36, border: '1px solid #c9d2dc',
            borderRadius: 6, padding: '0 10px', maxWidth: 360, color: T.muted, marginTop: 4, boxSizing: 'border-box' }}>
            <Search size={16} aria-hidden="true" />
            <input autoFocus value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Find a document" aria-label="Find a document"
              style={{ border: 0, outline: 0, flex: 1, fontSize: 14, minWidth: 0, background: 'transparent', color: T.text }} />
            {loading && <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />}
          </label>
        </div>

        <div style={{ overflow: 'auto', flex: 1 }}>
          <div role="table" aria-label="Documents" style={{ minWidth: 420 }}>
            <div role="row" style={{ display: 'grid', gridTemplateColumns: '40px minmax(0,1fr) 150px',
              height: 34, alignItems: 'center', background: T.band, color: T.muted, fontWeight: 600, fontSize: 13 }}>
              <span /><span style={{ padding: '0 10px' }}>Document</span><span style={{ padding: '0 10px' }}>Status</span>
            </div>
            {(res?.rows || []).length === 0 && !loading && (
              <div style={{ padding: 20, textAlign: 'center', color: T.muted, fontSize: 14 }}>No documents to choose from.</div>
            )}
            {(res?.rows || []).map(d => {
              const on = d._on_record || picked.some(p => String(p) === String(d.id))
              return (
                <label role="row" key={String(d.id)} style={{
                  display: 'grid', gridTemplateColumns: '40px minmax(0,1fr) 150px', alignItems: 'center',
                  height: 48, borderBottom: '1px solid #edf0f4', fontSize: 14, cursor: d._on_record ? 'default' : 'pointer',
                  background: d._on_record ? T.rowAlt : on ? '#f6f9ff' : undefined, color: d._on_record ? T.muted : undefined,
                }}>
                  <span style={{ padding: '0 10px' }}>
                    <input type={multi ? 'checkbox' : 'radio'} checked={on} disabled={d._on_record}
                      onChange={() => toggle(d.id)}
                      aria-label={d._on_record ? 'Already on this record' : `Select ${d.display || String(d.id)}`} />
                  </span>
                  <span style={{ padding: '0 10px', fontFamily: T.mono, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {d.display || String(d.id)}
                  </span>
                  <span style={{ padding: '0 10px' }}>
                    {d._on_record
                      ? <span style={{ ...chip, background: INFO_BAND.bg, color: '#0b4fa8' }}>On this record</span>
                      : <span style={{ ...chip, background: '#f0f3f7', color: '#3b4552' }}>Open</span>}
                  </span>
                </label>
              )
            })}
          </div>
        </div>

        <div style={dialogFoot}>
          <span style={{ fontSize: 14 }}>
            <b>{picked.length} {picked.length === 1 ? 'document' : 'documents'}</b> selected
            <span style={{ color: T.muted }}> · {noun}</span>
          </span>
          <span style={{ flex: 1 }} />
          <button type="button" onClick={onCancel} style={btn}>Cancel</button>
          {pickLines && (
            <button type="button" disabled={none} onClick={() => onChoose(picked, 'all')}
              style={{ ...btn, opacity: none ? 0.55 : 1, cursor: none ? 'not-allowed' : 'pointer' }}>
              Add all their lines
            </button>
          )}
          <button type="button" disabled={none} onClick={() => onChoose(picked, pickLines ? 'pick' : 'all')}
            style={{ ...btnPri, opacity: none ? 0.55 : 1, cursor: none ? 'not-allowed' : 'pointer' }}>
            {pickLines ? 'Choose lines…' : 'Add lines'}
          </button>
        </div>
      </div>
    </div>
  )
}
